import { CATEGORY_COLORS, getCategoryLabel } from "./CategoryChips";
import { Calendar, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";

interface ChunkItem {
  id: number;
  category: string;
  title: string;
  importance: number | null;
  createdAt: string | null;
  sourceType: string | null;
}

interface WeekGroup {
  weekStart: string;
  chunks: ChunkItem[];
  count: number;
}

interface TimelineViewProps {
  timeline: WeekGroup[];
  selectedNodeId: number | null;
  onSelectNode: (id: number) => void;
  isLoading: boolean;
}

function formatWeekLabel(weekStart: string): string {
  const d = new Date(weekStart + "T00:00:00");
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

export function TimelineView({ timeline, selectedNodeId, onSelectNode, isLoading }: TimelineViewProps) {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12" data-testid="timeline-loading">
        <Loader2 className="h-5 w-5 animate-spin text-indigo-500" />
      </div>
    );
  }

  if (timeline.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-12 text-center" data-testid="timeline-empty">
        <Calendar className="h-8 w-8 text-gray-300 mx-auto mb-2" />
        <p className="text-sm text-gray-500">No timeline data available</p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto pb-4 custom-scrollbar" data-testid="timeline-view">
      <div className="flex gap-4 min-w-max">
        {timeline.map(week => (
          <div key={week.weekStart} className="w-64 flex-shrink-0">
            <div className="flex items-center gap-2 mb-2 px-1">
              <div className="h-2 w-2 rounded-full bg-indigo-500" />
              <span className="text-xs font-semibold text-gray-700">
                Week of {formatWeekLabel(week.weekStart)}
              </span>
              <span className="text-[10px] text-gray-400 ml-auto">{week.count} nodes</span>
            </div>
            <div className="space-y-1.5 border-l-2 border-gray-200 pl-3 ml-1">
              {week.chunks.slice(0, 10).map(chunk => {
                const color = CATEGORY_COLORS[chunk.category] || "#6B7280";
                const isSelected = chunk.id === selectedNodeId;
                return (
                  <button
                    key={chunk.id}
                    onClick={() => onSelectNode(chunk.id)}
                    className={cn(
                      "w-full text-left p-2.5 rounded-lg border transition-all",
                      isSelected
                        ? "bg-indigo-50 border-indigo-200 ring-1 ring-indigo-200"
                        : "bg-white border-gray-100 hover:border-gray-200 hover:shadow-sm"
                    )}
                    data-testid={`timeline-chunk-${chunk.id}`}
                  >
                    <div className="flex items-start gap-2">
                      <span
                        className="w-2 h-2 rounded-full mt-1 flex-shrink-0"
                        style={{ backgroundColor: color }}
                      />
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-medium text-gray-900 truncate">{chunk.title}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <span
                            className="px-1 py-px rounded text-[8px] font-semibold text-white"
                            style={{ backgroundColor: color }}
                          >
                            {getCategoryLabel(chunk.category)}
                          </span>
                          {chunk.createdAt && (
                            <span className="text-[10px] text-gray-400">
                              {new Date(chunk.createdAt).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })}
              {week.chunks.length > 10 && (
                <p className="text-[10px] text-gray-400 pl-4">+{week.chunks.length - 10} more</p>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
